import {useStores} from '../stores/useStores';

import { observer } from "mobx-react-lite";
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { waitForTx } from '@waves/waves-transactions';

import {verifiedCollections} from '../constants/common';
import {NODE_URL_MAP} from 'src/constants';

import MarketItem from './MarketItem';
import MarketEmptyItem from './MarketEmptyItem';

import "./Address/styles.css";
import { Helmet } from 'react-helmet-async';
import CardStore from 'src/stores/CardStore';

import {ReactComponent as VerifiedIcon} from '../assets/icons/verified.svg';
import Tabs from './Tabs';
import Button from './Button';
import SizedBox from './SizedBox';

import { getVoteById, IResultById } from './DaoPage/Data';

const MAINNET_ADDRESS = NODE_URL_MAP.W;

enum TABS {
    ON_SALE='onSale',
    OWNED='owned'
}

interface IMarketplaceProps {
    location?: Location;
}

export default observer(function(props: IMarketplaceProps) {
    const { walletStore, accountStore, notificationStore } = useStores();
    let { collection, address } = useParams();

    const [version, setVersion] = useState<number>(0);

    const [onSaleNft, setOnSaleNft] = useState<any[]>([]);
    const [ownedNft, setOwnedNft] = useState<any[]>([]);

    const [activeTab, setActiveTab] = useState<TABS>(TABS.ON_SALE);

    const [userLoaded, setUserLoaded] = useState<boolean>(false);

    const [voteData, setVoteData] = useState<IResultById|null>();
    const [voteVersion, setVoteVersion] = useState<number>(0);

    useEffect(() => {
        // load origin
        walletStore.loadCollection(collection as string, address as string).then((data) => {
            const onSale = data.filter(item => item.isOnSell);
            const owned = data.filter(item => !item.isOnSell);
            setOnSaleNft(onSale);
            setOwnedNft(owned);
            if (owned.length && !onSale.length) {
                setActiveTab(TABS.OWNED);
            }
            setUserLoaded(true);
        });

        return () => setVersion(0);
    }, [version]);

    useEffect(() => {
        getVoteById(`${address}_${collection}`).then((result) => {
            setVoteData(result);
        });
    }, [voteVersion]);

    let onSaleArr = [...onSaleNft];
    let ownedArr = [...ownedNft];

    if (walletStore.searchValue) {
        const filterFn = (item: CardStore) => {
            const name = item.name.toLowerCase();
            return name.indexOf(walletStore.searchValue) >= 0 ||
                (walletStore.searchValue.indexOf('3P') === 0 && item.seller.indexOf(walletStore.searchValue) >= 0);
        }
        onSaleArr = onSaleArr.filter(filterFn);
        ownedArr = ownedArr.filter(filterFn);
    } else {
        onSaleArr = [...onSaleNft];
        ownedArr = [...ownedNft];
    }

    const tabs = [{
        id: TABS.ON_SALE,
        title: 'On sale',
        count: onSaleArr.length || 0,
        active: activeTab === TABS.ON_SALE,
        onClick: (id: string) => {setActiveTab(id as TABS)}
    }, {
        id: TABS.OWNED,
        title: 'Off sale',
        count: ownedArr.length || 0,
        active: activeTab === TABS.OWNED,
        onClick: (id: string) => {setActiveTab(id as TABS)}
    }];

    const toVoting = async (collectionId: string, fn: string) => {
        if (!collectionId) return;
        try {
            const txId = await accountStore.invoke({
                dApp: '3P975LUuqc7uBPiSxp3nemf2FZh62zfirSh',
                payment: [],
                call: {
                    function: fn,
                    args: [
                        {type: 'string', value: collectionId}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            if (txId) {
                setVoteVersion(voteVersion + 1);
                notificationStore.notify(`Completed!`, {
                    type: "success",
                    title: fn === 'sendToVote' ? 'The collection has been submitted for voting' : 'Collection removed from voting',
                    link: `https://wavesexplorer.com/tx/${txId}`,
                    linkTitle: "View on Explorer",
                });
            }
        } catch (e: any) {
            console.error(e);
            notificationStore.notify(e.message ?? JSON.stringify(e), {
                type: 'error',
                title: 'Transaction is not completed'
            });
        }
    }

    const isUserCollection = accountStore.address === address;
    const whiteListVerify = address && verifiedCollections.indexOf(`${address}_${collection}`) >= 0;

    return (
        <div className='nftMarketplace-Address'>
            <Helmet>
                <title>Puzzle NFT Market - Collection: "{collection}"</title>
                <meta name="description" content={'Puzzle NFT Market collection "' + collection + '"'} />
                <meta property="og:title" content={'Puzzle NFT Market - Collection: ' + collection} />
            </Helmet>
            <div className='nftMarketplace-Address__accountWrap'>
                <div className="nftMarketplace-Address__name" title={collection}>
                    Collection: "{collection}"
                    { whiteListVerify || voteData?.confirmed ? <VerifiedIcon className='nftMarketplace-Address__nameVerified' title='Verified' /> : '' }
                    <SizedBox width={8}/>
                    {
                        isUserCollection && !whiteListVerify && voteData === null ?
                            <Button size='small' onClick={() => {toVoting(address + '_' + collection, 'sendToVote')}}>To voting</Button> : ''
                    }
                    {
                        isUserCollection && !whiteListVerify && !voteData?.confirmed && !voteData?.rejected && voteData?.voteId ?
                            <Button size='small' kind='secondary' onClick={() => {toVoting(voteData.voteId as string, 'removeFromVote')}}>Unvote</Button> : ''
                    }
                </div>
            </div>
            <Tabs tabs={tabs} />
            <div className='nftMarketplace-Address__catalog'>
                <div className='puzzle-nftMarketplace'>
                    {
                        activeTab === TABS.ON_SALE ?
                            (onSaleArr.length ? 
                                onSaleArr.map((item) => <MarketItem key={item.assetId} item={item} cb={() => setVersion(version + 1)} />) :
                                (!userLoaded ? <MarketEmptyItem key={1}/> : '')
                            ) :
                            ''
                    }
                    {
                        activeTab === TABS.OWNED ? 
                            (ownedArr.length ?
                                ownedArr.map((item) => <MarketItem key={item.assetId} item={item} cb={() => setVersion(version + 1)} />) :
                                (!userLoaded ? <MarketEmptyItem key={1}/> : '')
                            ) : 
                            ''
                    }
                </div>
            </div>
        </div>
    );
});
